import { useState, useEffect } from 'react'
import { useAuth } from '../../../context/AuthContext'
import ImagePicker from '../../ondemand-library/content-catalog/ImagePicker'

// Single-item "create catalog from clip" modal used by the Mux Editor.
// Caller provides a `buildPayload(title)` function so the modal stays
// agnostic about whether the source is a real Mux clip asset or an
// instant-clip URL (different payload shape, same UX).
export default function CreateCatalogFromClipModal({
  initialTitle = '',
  sourceInfo = '',
  buildPayload,
  onClose,
  onComplete,
}) {
  const { api } = useAuth()
  const [title, setTitle] = useState(initialTitle)
  // Optional override pulled from CF Images; empty string = use the default
  // thumbnail produced by the parent's buildPayload (Mux auto-thumb).
  const [thumbnailOverride, setThumbnailOverride] = useState('')
  const [busy, setBusy] = useState(false)
  const [result, setResult] = useState(null) // { ok, tbtvId, error }
  // Existing catalog titles (lowercased + trimmed) for duplicate detection
  const [existingTitles, setExistingTitles] = useState(null)

  useEffect(() => {
    let cancelled = false
    ;(async () => {
      try {
        const data = await api('/vod')
        if (cancelled) return
        const set = new Set(
          (data.items || []).map(i => (i.title || '').trim().toLowerCase()).filter(Boolean)
        )
        setExistingTitles(set)
      } catch {
        // Non-blocking — if this fails, just skip the check rather than block creation
        if (!cancelled) setExistingTitles(new Set())
      }
    })()
    return () => { cancelled = true }
  }, [api])

  const trimmedLower = title.trim().toLowerCase()
  const isDuplicate = !!existingTitles && trimmedLower && existingTitles.has(trimmedLower)
  const titlesLoading = existingTitles === null

  async function handleSubmit() {
    const finalTitle = title.trim()
    if (!finalTitle || isDuplicate) return
    setBusy(true)
    setResult(null)
    try {
      const payload = buildPayload(finalTitle, thumbnailOverride || null)
      const res = await api('/vod/create-linked', {
        method: 'POST',
        body: JSON.stringify(payload),
      })
      setResult({ ok: true, tbtvId: res.tbtv_id })
      onComplete?.(res.tbtv_id)
    } catch (e) {
      setResult({ ok: false, error: e.message })
    }
    setBusy(false)
  }

  return (
    <div className="mux-modal-backdrop" onClick={busy ? undefined : onClose}>
      <div className="mux-modal" onClick={e => e.stopPropagation()} style={{ maxWidth: 540 }}>
        <div className="mux-modal-header">
          <h3>Create Catalog Item</h3>
          {!busy && <button className="btn btn-ghost btn-sm" onClick={onClose}>✕</button>}
        </div>

        <div className="mux-modal-body">
          {sourceInfo && (
            <div style={{ marginBottom: 12, fontSize: 12, color: 'var(--text-muted)' }}>
              {sourceInfo}
            </div>
          )}

          <div className="mux-editor-field" style={{ marginBottom: 12 }}>
            <label style={{ display: 'block', marginBottom: 4, fontSize: 12 }}>Title</label>
            <input
              type="text"
              value={title}
              onChange={e => setTitle(e.target.value)}
              disabled={busy || result?.ok}
              onKeyDown={e => { if (e.key === 'Enter' && !busy && !result?.ok && !isDuplicate) handleSubmit() }}
              style={{
                width: '100%',
                borderColor: isDuplicate ? '#ef4444' : undefined,
              }}
              placeholder="Catalog item title"
              autoFocus
            />
            {isDuplicate && (
              <div style={{ color: '#ef4444', fontSize: 11, marginTop: 4 }}>
                A catalog item with this title already exists. Pick a different title.
              </div>
            )}
          </div>

          <div className="mux-editor-field" style={{ marginBottom: 12 }}>
            <label style={{ display: 'block', marginBottom: 4, fontSize: 12 }}>
              Thumbnail override <span style={{ color: 'var(--text-muted)', fontWeight: 'normal' }}>(optional — defaults to Mux auto-thumbnail)</span>
            </label>
            <ImagePicker
              value={thumbnailOverride}
              onChange={setThumbnailOverride}
              placeholder="Paste URL or browse CF Images"
            />
          </div>

          <div style={{ fontSize: 11, color: 'var(--text-muted)', marginBottom: 12 }}>
            Will be created as a <strong>draft</strong>. Edit description, tags, and release date afterwards in Content Catalog.
          </div>

          {result && (
            result.ok ? (
              <div style={{ marginBottom: 12, padding: 8, background: 'rgba(34,197,94,0.1)', border: '1px solid rgba(34,197,94,0.3)', borderRadius: 4, fontSize: 12 }}>
                ✓ Catalog item created (draft) — <code>{result.tbtvId}</code>
              </div>
            ) : (
              <div style={{ marginBottom: 12, padding: 8, background: 'rgba(239,68,68,0.1)', border: '1px solid rgba(239,68,68,0.3)', borderRadius: 4, fontSize: 12 }}>
                ✕ {result.error}
              </div>
            )
          )}

          <div className="mux-restore-action">
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 8 }}>
              {result?.ok ? (
                <button className="btn-primary" onClick={onClose}>Done</button>
              ) : (
                <>
                  <button className="btn btn-ghost btn-sm" onClick={onClose} disabled={busy}>Cancel</button>
                  <button
                    className="btn-primary"
                    onClick={handleSubmit}
                    disabled={busy || !title.trim() || isDuplicate || titlesLoading}
                    title={titlesLoading ? 'Checking for duplicate titles…' : undefined}
                  >
                    {busy ? 'Creating…' : 'Create Catalog Item'}
                  </button>
                </>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}
